import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import Select from "react-select";
import { useToast } from "../../../context/ToastContext";
// import LiveChat from "../other/LiveChat";
import { LoginURL, checkEmailURL, countryList, globalSignup } from "../../assets/api/BaseURL";

const Login = () => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { setPlan, setCurrency } = useToast();

  const [view, setView] = useState(null);
  const [user, setUser] = useState({
    username: "",
    password: "",
    confirmPassword: "",
    email: "",
    country: "",
    countryCode: "",
    currencySymbol: "",
    currencyCode: "",
    restaurantName: ""
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState("email"); // "email", "password", "signup"
  const [emailExists, setEmailExists] = useState(null);
  const [countries, setCountries] = useState([]);
  const [checkingEmail, setCheckingEmail] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState(null);
  
  // Add state for password validation
  const [passwordError, setPasswordError] = useState("");
  const [confirmPasswordError, setConfirmPasswordError] = useState("");

  useEffect(() => {
    if (
      localStorage.getItem("token") &&
      localStorage.getItem("planDetails") &&
      localStorage.getItem("userId") &&
      localStorage.getItem("currencySymbol")
    ) {
      navigate("/fooders_cp");
      setView(false);
    } else {
      localStorage.removeItem("token");
      localStorage.removeItem("planDetails");
      localStorage.removeItem("userId");
      localStorage.removeItem("currencySymbol");
      setView(true);
    }
  }, [navigate]);

  // Fetch countries list from your API
  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const response = await axios.get(countryList);

        if (response.data.status === "success" && response.data.data) {
          const countryListData = response.data.data.map(country => ({
            id: country.id,
            value: country.country_code,
            label: `${country.country_name}`,
            code: country.country_code,
            name: country.country_name,
            currency_symbol: country.currency_symbol,
            currency_code: country.currency_code
          })).sort((a, b) => a.label.localeCompare(b.label));

          setCountries(countryListData);
        } else {
          console.error("Failed to fetch countries");

        }
      } catch (error) {
        console.error("Error fetching countries:", error);

      }
    };
    fetchCountries();
  }, []);

  // Add password validation function
  const validatePassword = (password) => {
    if (password && password.length < 6) {
      setPasswordError("Password must be at least 6 characters");
      return false;
    } else {
      setPasswordError("");
      return true;
    }
  };

  // Add confirm password validation function
  const validateConfirmPassword = (confirmPassword, password) => {
    if (confirmPassword && confirmPassword !== password) {
      setConfirmPasswordError("Passwords do not match");
      return false;
    } else if (confirmPassword && confirmPassword === password) {
      setConfirmPasswordError("");
      return true;
    } else {
      setConfirmPasswordError("");
      return true;
    }
  };

  const handleInput = (e) => {
    const { name, value } = e.target;
    setUser(prev => ({ ...prev, [name]: value }));

    // Clear error when user types
    if (error) setError("");

    // Reset email existence when email changes
    if (name === "email") {
      setEmailExists(null);
      setStep("email");
    }
    
    // Password validation with real-time checking
    if (name === "password") {
      validatePassword(value);
      // If confirm password field has value, validate it against new password
      if (user.confirmPassword) {
        validateConfirmPassword(user.confirmPassword, value);
      }
    }
    
    // Confirm password validation with real-time checking
    if (name === "confirmPassword") {
      validateConfirmPassword(value, user.password);
    }
  };

  const handleCountryChange = (selectedOption) => {
    console.log(selectedOption)
    setSelectedCountry(selectedOption);
    setUser(prev => ({
      ...prev,
      country: selectedOption?.name || "",
      countryCode: selectedOption?.code || "",
      currencySymbol: selectedOption?.currency_symbol || "",
      currencyCode: selectedOption?.currency_code || ""
    }));

    // Clear country error if exists
    if (error === "Please select your country") {
      setError("");
    }
  };

  const checkEmail = async (email) => {
    if (!email) {
      setError("Please enter your email");
      return false;
    }

    // const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    // if (!emailRegex.test(email)) {
    //   setError("Please enter a valid email address");
    //   return false;
    // }

    setCheckingEmail(true);
    setError("");

    try {
      const response = await axios.post(checkEmailURL, { email });

      if (response.data) {
        setEmailExists(response.data.exists);
        if (response.data.exists) {
          setStep("password");
        } else {
          setStep("signup");
        }
        return true;
      } else {
        setError(response.data?.message || "Error checking email");
        return false;
      }
    } catch (error) {
      console.error("Error checking email:", error);
      if (error.response) {
        setError(error.response.data?.message || "Server error. Please try again.");
      } else if (error.request) {
        setError("Network error. Please check your connection.");
      } else {
        setError("Error checking email. Please try again.");
      }
      return false;
    } finally {
      setCheckingEmail(false);
    }
  };

  const handleContinueEmail = async () => {
    await checkEmail(user.email);
  };

  const handleEmailStepSubmit = (e) => {
    e.preventDefault();
    if (step !== "email" || checkingEmail || !user.email?.trim()) return;
    handleContinueEmail();
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!user.password) {
      setError("Please enter your password");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const response = await axios.post(LoginURL, {
        username: user.email,
        password: user.password
      });

      if (
        response.status === 200 &&
        response.data.token &&
        response.data.currency_symbol &&
        response.data.plan &&
        response.data.encryptedId
      ) {
        localStorage.setItem("token", response.data.token);
        localStorage.setItem("currencySymbol", response.data.currency_symbol || "");
        if (response.data.currency_code != null && response.data.currency_code !== "") {
          localStorage.setItem("currencyCode", response.data.currency_code);
        }
        if (setCurrency) {
          setCurrency(response.data.currency_symbol || "", response.data.currency_code || "");
        }
        localStorage.setItem(
          "planDetails",
          JSON.stringify(response.data.plan)
        );
        localStorage.setItem(
          "userId",
          JSON.stringify(response.data.encryptedId)
        );
        localStorage.setItem(
          "login_type",
          response.data.login_type
        );

        setPlan(response.data.plan);
        showToast && showToast("Login successful!", "success");
        navigate("/fooders_cp");
      } else {
        setUser({ username: "", password: "" });
      }
    } catch (error) {
      console.error("Error during login:", error);
      if (error.response) {
        setError(error.response.data?.message || "Login failed. Please try again.");
      } else if (error.request) {
        setError("Network error. Please check your connection.");
      } else {
        setError("Error logging in. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const validateSignup = () => {
    // Validate restaurant name
    if (!user.restaurantName.trim()) {
      setError("Please enter your restaurant name");
      return false;
    }

    if (user.restaurantName.trim().length < 3) {
      setError("Restaurant name must be at least 3 characters");
      return false;
    }

    // Validate password
    if (!user.password) {
      setError("Please enter your password");
      return false;
    }

    if (user.password.length < 6) {
      setError("Password must be at least 6 characters");
      return false;
    }

    // Validate confirm password
    if (!user.confirmPassword) {
      setError("Please confirm your password");
      return false;
    }

    if (user.password !== user.confirmPassword) {
      setError("Passwords do not match");
      return false;
    }

    // Validate country
    if (!user.country) {
      setError("Please select your country");
      return false;
    }

    // Validate terms and conditions
    if (!termsAccepted) {
      setError("Please accept the Terms & Conditions");
      return false;
    }

    return true;
  };

  const handleSignup = async (e) => {
    e.preventDefault();

    if (!validateSignup()) {
      return;
    }

    setLoading(true);
    setError("");

    try {
      const response = await axios.post(globalSignup, {
        email: user.email,
        password: user.password,
        restaurant_name: user.restaurantName.trim(),
        country: user.country,
        country_code: user.countryCode,
        currency_symbol: user.currencySymbol,
        currency_code: user.currencyCode,
        username: user.email.split('@')[0]
      });

      if (response.data.status) {
        localStorage.setItem("token", response.data.token);
        localStorage.setItem("userId", response.data.userId);
        localStorage.setItem("planDetails", JSON.stringify(response.data.planDetails));
        localStorage.setItem("currencySymbol", response.data.currencySymbol);
        localStorage.setItem("login_type", 'fooder');


        if (setCurrency) {
          setCurrency(response.data.currencySymbol || "", response.data.currencyCode || "");
        }



        if (response.data.planDetails) {
          setPlan(response.data.planDetails);
        }

        showToast && showToast("Account created successfully!", "success");
        navigate("/fooders_cp");
      } else {
        setError(response.data?.message || "Error creating account");
      }
    } catch (error) {
      console.error("Error during signup:", error);
      if (error.response) {
        setError(error.response.data?.message || "Signup failed. Please try again.");
      } else if (error.request) {
        setError("Network error. Please check your connection.");
      } else {
        setError("Error creating account. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const resetToEmailStep = () => {
    setStep("email");
    setEmailExists(null);
    setSelectedCountry(null);
    setUser({
      ...user,
      password: "",
      confirmPassword: "",
      country: "",
      countryCode: "",
      currencySymbol: "",
      currencyCode: "",
      restaurantName: ""
    });
    setTermsAccepted(false);
    setError("");
    setPasswordError("");
    setConfirmPasswordError("");
  };

  // Custom styles for react-select
  const customSelectStyles = {
    control: (provided, state) => ({
      ...provided,
      height: '48px',
      borderRadius: '0.25rem',
      borderColor: state.isFocused ? '#80bdff' : '#ced4da',
      boxShadow: state.isFocused ? '0 0 0 0.2rem rgba(0,123,255,0.25)' : 'none',
      '&:hover': {
        borderColor: '#80bdff'
      }
    }),
    menu: (provided) => ({
      ...provided,
      zIndex: 9999
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected ? '#007bff' : state.isFocused ? '#e9ecef' : 'white',
      color: state.isSelected ? 'white' : '#212529',
      '&:active': {
        backgroundColor: state.isSelected ? '#007bff' : '#e9ecef'
      }
    }),
    placeholder: (provided) => ({
      ...provided,
      color: '#6c757d'
    })
  };

  return (
    <>
      {view && (
        <>
          <div className="container-scroller">
            <div className="container-fluid page-body-wrapper full-page-wrapper px-0">
              <div className="content-wrapper d-flex align-items-center auth px-0">
                <div className="row w-100 mx-0">
                  <div className="text-center mb-2">
                    <img
                      src="images/vyfoo_logo.png"
                      alt="logo"
                      className="loginlogo"
                    />
                  </div>
                  <div className="col-lg-4 mx-auto">
                    <div className="auth-form-light text-left py-5 px-4 px-sm-5">
                      <h4>Hello! let's get started</h4>
                      <h6 className="fw-light">
                        {step === "email" && "Enter your email to continue"}
                        {step === "password" && "Enter your password"}
                        {step === "signup" && "Create your account"}
                      </h6>

                      <form className="pt-3" onSubmit={
                        step === "password" ? handleLogin :
                          step === "signup" ? handleSignup :
                            handleEmailStepSubmit
                      }>
                        {error && (
                          <div className="d-flex justify-content-between align-items-start alert alert-danger">
                            <div className="vsalign">{error}</div>
                            <div className="vsalign">
                              <span
                                className=".h3 text-muted"
                                style={{ cursor: "pointer" }}
                                onClick={() => setError("")}
                              >
                                x
                              </span>
                            </div>
                          </div>
                        )}

                        {/* Email field */}
                        <div className="form-group">
                          <input
                           
                            className="form-control form-control-lg"
                            placeholder="Email address"
                            name="email"
                            value={user.email}
                            onChange={handleInput}
                            disabled={step !== "email" || checkingEmail}
                            autoFocus={step === "email"}
                          />
                          {checkingEmail && (
                            <small className="text-muted">
                              <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                              Checking email...
                            </small>
                          )}
                        </div>

                        {/* Continue button for email step */}
                        {step === "email" && (
                          <div className="mt-3">
                            <button
                              type="submit"
                              className="btn btn-block btn-primary btn-lg font-weight-medium auth-form-btn col-12"
                              disabled={!user.email || checkingEmail}
                            >
                              {checkingEmail ? (
                                <span>
                                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                  Checking...
                                </span>
                              ) : (
                                "CONTINUE"
                              )}
                            </button>
                          </div>
                        )}

                        {/* Password field for login */}
                        {step === "password" && (
                          <>
                            <div className="form-group">
                              <input
                                type="password"
                                className="form-control form-control-lg"
                                placeholder="Password"
                                name="password"
                                value={user.password}
                                onChange={handleInput}
                                disabled={loading}
                                autoFocus
                              />
                            </div>
                            <div className="my-2 d-flex justify-content-end align-items-center">
                              <Link to="/forgot_password">
                                <span className="auth-link text-black">
                                  Forgot password?
                                </span>
                              </Link>
                            </div>
                          </>
                        )}

                        {/* Signup fields */}
                        {step === "signup" && (
                          <>
                            {/* Restaurant Name */}
                            <div className="form-group">
                              <input
                                type="text"
                                className="form-control form-control-lg"
                                placeholder="Restaurant Name *"
                                name="restaurantName"
                                value={user.restaurantName}
                                onChange={handleInput}
                                disabled={loading}
                                autoFocus
                              />
                            </div>

                            {/* Password with validation message */}
                            <div className="form-group">
                              <input
                                type="password"
                                className={`form-control form-control-lg ${passwordError ? 'is-invalid' : ''}`}
                                placeholder="Password *"
                                name="password"
                                value={user.password}
                                onChange={handleInput}
                                disabled={loading}
                              />
                              {passwordError && (
                                <small className="text-danger d-block mt-1">
                                  {passwordError}
                                </small>
                              )}
                              {!passwordError && user.password && (
                                <small className="text-success d-block mt-1">
                                  ✓ Password strength is good
                                </small>
                              )}
                              {/* <small className="text-muted d-block mt-1">
                                Password must be at least 6 characters
                              </small> */}
                            </div>

                            {/* Confirm Password with validation message */}
                            <div className="form-group">
                              <input
                                type="password"
                                className={`form-control form-control-lg ${confirmPasswordError ? 'is-invalid' : (user.confirmPassword && !confirmPasswordError ? 'is-valid' : '')}`}
                                placeholder="Confirm Password *"
                                name="confirmPassword"
                                value={user.confirmPassword}
                                onChange={handleInput}
                                disabled={loading}
                              />
                              {confirmPasswordError && (
                                <small className="text-danger d-block mt-1">
                                  {confirmPasswordError}
                                </small>
                              )}
                              {user.confirmPassword && !confirmPasswordError && (
                                <small className="text-success d-block mt-1">
                                  ✓ Passwords match
                                </small>
                              )}
                            </div>

                            {/* Country Dropdown using React Select */}
                            <div className="form-group">
                              <Select
                                options={countries}
                                value={selectedCountry}
                                onChange={handleCountryChange}
                                placeholder="Select Country *"
                                isDisabled={loading}
                                styles={customSelectStyles}
                                className="react-select-container"
                                classNamePrefix="react-select"
                                isSearchable={true}
                                noOptionsMessage={() => "No countries found"}
                                loadingMessage={() => "Loading countries..."}
                              />
                              {/* {user.country && (
                                <small className="text-muted mt-1 d-block">
                                  Currency: {user.currencySymbol} ({user.currencyCode})
                                </small>
                              )} */}
                            </div>

                            {/* Terms and Conditions Checkbox */}
                            <div className="form-group">
                              <div className="form-check">
                                <input
                                  type="checkbox"
                                  className="form-check-input"
                                  id="termsCheckbox"
                                  checked={termsAccepted}
                                  onChange={(e) => {
                                    setTermsAccepted(e.target.checked);
                                    if (error === "Please accept the Terms & Conditions") {
                                      setError("");
                                    }
                                  }}
                                  disabled={loading}
                                />
                                <label className="form-check-label" htmlFor="termsCheckbox">
                                  I accept the{" "}
                                  <a href="https://vyfoo.com/terms/" target="_blank" rel="noopener noreferrer">
                                    Terms & Conditions
                                  </a>
                                </label>
                              </div>
                            </div>
                          </>
                        )}

                        {/* Submit button */}
                        {(step === "password" || step === "signup") && (
                          <div className="mt-3">
                            <button
                              type="submit"
                              className="btn btn-block btn-primary btn-lg font-weight-medium auth-form-btn col-12"
                              disabled={loading || (step === "signup" && (!termsAccepted || passwordError || confirmPasswordError))}
                            >
                              {loading ? (
                                <span>
                                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                  Processing...
                                </span>
                              ) : (
                                step === "password" ? "SIGN IN" : "CREATE ACCOUNT"
                              )}
                            </button>
                          </div>
                        )}

                        {/* Back button */}
                        {(step === "password" || step === "signup") && (
                          <div className="text-center mt-3">
                            <button
                              type="button"
                              className="btn btn-link"
                              onClick={resetToEmailStep}
                              disabled={loading}
                            >
                              ← Use different email
                            </button>
                          </div>
                        )}

                        <div className="text-center mt-2 fw-light">
                          <p style={{ fontSize: "12px" }}>
                            Made with ❤️ by <a href="https://vyqda.com/" target="_blank" rel="noreferrer">Vyqda</a> Technologies
                          </p>
                        </div>
                      </form>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default Login;